import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { enumValidator } from '@shared/src/components/core/form/validations/AutoCompleteValidator';
import { HomeController } from '@shared/src/controllers/home/home.controller';
import { ECMRDeliveryLineDto } from '@shared/src/dtos/route/cmr/ecmrparts/ECMRDeliveryLineDto';
import { PalletTypes } from '@shared/src/enums/PalletTypes';

@Component({
    selector: 'shared-ecmr-delivery-line',
    templateUrl: './shared.ecmr.delivery.line.component.html',
    styleUrls: ['./shared.ecmr.delivery.line.component.scss']
})
export class SharedECMRDeliveryLineComponent implements OnInit {

    @Output() valueChange: EventEmitter<ECMRDeliveryLineDto> = new EventEmitter<ECMRDeliveryLineDto>();
    @Output() expandedLineIdChanged: EventEmitter<number> = new EventEmitter<number>();
    @Output() onDeleteSelected: EventEmitter<ECMRDeliveryLineDto> = new EventEmitter<ECMRDeliveryLineDto>();
    @Output() onDelete: EventEmitter<ECMRDeliveryLineDto> = new EventEmitter<ECMRDeliveryLineDto>();

    public rFormCMRDeliveryLine: FormGroup;

    @Input() public readonly: boolean;

    private _expandedLine: boolean;
    @Input() public set expandedLine(value: boolean) {
        if (this._expandedLine === value)
            return;
        this._expandedLine = value;
    }
    public get expandedLine(): boolean {
        return this._expandedLine;
    }

    onExpandedLine() {
        this.expandedLine = !this.expandedLine;
        if (this.lineObject) {
            if (this.expandedLine)
                this.expandedLineIdChanged.next(this.lineObject.line);
            else
                this.expandedLineIdChanged.next(0);
        }
    }

    private _deleteSelected: ECMRDeliveryLineDto;
    @Input() public set deleteSelected(value: ECMRDeliveryLineDto) {
        if (this._deleteSelected === value)
            return;
        this._deleteSelected = value;
    }
    public get deleteSelected(): ECMRDeliveryLineDto {
        return this._deleteSelected;
    }

    showDeleteButton(): boolean {
        return !this.deleteSelected || (this.lineObject != null && this.deleteSelected !== this.lineObject);
    }

    onDeleteSelectedEvent(value: ECMRDeliveryLineDto) {
        this.onDeleteSelected.next(value);
    }

    onDeleteLine() {
        if (this.readonly)
            return;
        if (this.lineObject)
            this.onDelete.next(this.lineObject);
    }

    enumPalletTypes = PalletTypes;

    public useIonic: boolean = false;

    constructor(private fb: FormBuilder,
        @Inject("HomeController") protected homeController: HomeController,
        protected translateService: TranslateService) {
        this.rFormCMRDeliveryLine = fb.group({
            'reference': ['', [Validators.nullValidator]],
            'description': ['', [Validators.nullValidator]],
            'wDimension': ['', Validators.min(0)],
            'yDimension': ['', Validators.min(0)],
            'hDimension': ['', Validators.min(0)],
            'm3': ['', Validators.min(0)],
            'quantity': ['', [Validators.nullValidator]],
            'kg': ['', Validators.min(0)],
            'palletCount': ['', [Validators.nullValidator]],
            'palletType': [PalletTypes.None, [Validators.required, enumValidator(this.enumPalletTypes, false)]]
        });

        if (homeController.useIonic) this.useIonic = homeController.useIonic();
    }

    private _lineObject: ECMRDeliveryLineDto;

    public get lineObject(): ECMRDeliveryLineDto {
        return this._lineObject;
    }

    @Input()
    public set lineObject(value: ECMRDeliveryLineDto) {
        if (this._lineObject === value)
            return;
        this._lineObject = value;
        this.loadDataValues();
    }
    
    getResourcePalletType(type: PalletTypes): string{
        if(type == null || type == PalletTypes.None)
            return "";
        return this.translateService.instant("PALLETTYPES." + type.toUpperCase());
    }

    ngOnInit(): void {

    }

    private __reference: string;
    public get _reference(): string {
        return this.__reference;
    }
    public set _reference(value: string) {
        if (this.__reference === value)
            return;
        this.__reference = value;
        this.valueChangedEvent();
    }

    private __description: string;
    public get _description(): string {
        return this.__description;
    }
    public set _description(value: string) {
        if (this.__description === value)
            return;
        this.__description = value;
        this.valueChangedEvent();
    }

    private __palletType: PalletTypes;
    public get _palletType(): PalletTypes {
        return this.__palletType;
    }
    public set _palletType(value: PalletTypes) {
        if (this.__palletType === value)
            return;
        this.__palletType = value;
        this.valueChangedEvent();
    }

    private __wDimension: number;
    public get _wDimension(): number {
        return this.__wDimension;
    }
    public set _wDimension(value: number) {
        if (this.__wDimension === value)
            return;
        this.__wDimension = value;
        this.valueChangedEvent();
    }

    private __yDimension: number;
    public get _yDimension(): number {
        return this.__yDimension;
    }
    public set _yDimension(value: number) {
        if (this.__yDimension === value)
            return;
        this.__yDimension = value;
        this.valueChangedEvent();
    }
    
    private __hDimension: number;
    public get _hDimension(): number {
        return this.__hDimension;
    }
    public set _hDimension(value: number) {
        if (this.__hDimension === value)
            return;
        this.__hDimension = value;
        this.valueChangedEvent();
    }
    
    private __m3: number;
    public get _m3(): number {
        return this.__m3;
    }
    public set _m3(value: number) {
        if (this.__m3 === value)
            return;
        this.__m3 = value;
        this.valueChangedEvent();
    }
    
    private __quantity: number;
    public get _quantity(): number {
        return this.__quantity;
    }
    public set _quantity(value: number) {
        if (this.__quantity === value)
            return;
        this.__quantity = value;
        this.valueChangedEvent();
    }

    private __kg: number;
    public get _kg(): number {
        return this.__kg;
    }
    public set _kg(value: number) {
        if (this.__kg === value)
            return;
        this.__kg = value;
        this.valueChangedEvent();
    }

    private __palletCount: number;
    public get _palletCount(): number {
        return this.__palletCount;
    }
    public set _palletCount(value: number) {
        if (this.__palletCount === value)
            return;
        this.__palletCount = value;
        this.valueChangedEvent();
    }

    public valueChangedEvent() {
        if (this.readonly)
            return;
        let haschanges = this.lineObject.reference != this._reference;
        haschanges = this.lineObject.description != this._description || haschanges;
        haschanges = this.lineObject.palletType != this._palletType || haschanges;
        haschanges = this.lineObject.wDimension != this._wDimension || haschanges;
        haschanges = this.lineObject.yDimension != this._yDimension || haschanges;
        haschanges = this.lineObject.hDimension != this._hDimension || haschanges;
        haschanges = this.lineObject.m3 != this._m3 || haschanges;
        haschanges = this.lineObject.quantity != this._quantity || haschanges;
        haschanges = this.lineObject.kg != this._kg || haschanges;
        haschanges = this.lineObject.palletCount != this._palletCount || haschanges;
        if(haschanges){
            this.loadFrontToObject();
            this.valueChange.next(this.lineObject);
        }
    }

    loadFrontToObject(){
        this._lineObject.reference = this._reference;
        this._lineObject.description = this._description;
        this._lineObject.palletType = this._palletType;
        this._lineObject.wDimension = this._wDimension;
        this._lineObject.yDimension = this._yDimension;
        this._lineObject.hDimension = this._hDimension;
        this._lineObject.m3 = this._m3;
        this._lineObject.quantity = this._quantity;
        this._lineObject.kg = this._kg;
        this._lineObject.palletCount = this._palletCount;
    }
    public loadDataValues() {
        if(this.lineObject == null)
            return;
        this.__reference = this.lineObject.reference;
        this.__description = this.lineObject.description;
        this.__palletType = this.lineObject.palletType;
        this.__wDimension = this.lineObject.wDimension;
        this.__yDimension = this.lineObject.yDimension;
        this.__hDimension = this.lineObject.hDimension;
        this.__m3 = this.lineObject.m3;
        this.__quantity = this.lineObject.quantity;
        this.__kg = this.lineObject.kg;
        this.__palletCount = this.lineObject.palletCount;
    }
}
